import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, LayoutDashboard, LogOut, Building2, Clock, Sparkles } from "lucide-react";

// Market Pulse launched Sep 2026 — the "New" badge auto-retires ~45 days later.
const PULSE_NEW = new Date() < new Date("2026-10-17");
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { api, ADMIN_FIRM_KEY } from "@/lib/api";

export const Navbar = ({ dark = false }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [branding, setBranding] = useState(null);
  const text = dark ? "text-white" : "text-ally-ink";
  const sub = dark ? "text-white/70" : "text-ally-slate";

  useEffect(() => {
    const isEnt = user && (user.role === "enterprise" || (user.role === "superadmin" && localStorage.getItem(ADMIN_FIRM_KEY)));
    if (!isEnt) { setBranding(null); return; }
    api.get("/enterprise/plan").then((r) => setBranding(r.data.branding)).catch(() => setBranding(null));
  }, [user, location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    const inPortal = location.pathname.startsWith("/enterprise");
    const accent = branding?.accent;
    const toRgb = (hex) => {
      const h = hex.replace("#", "");
      const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
      const n = parseInt(f, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    };
    if (branding?.white_label && accent && inPortal) {
      const [r, g, b] = toRgb(accent);
      const dark = (c) => Math.round(c * 0.82);
      const tint = (c) => Math.round(c + (255 - c) * 0.87);
      root.style.setProperty("--ally-pink", `${r} ${g} ${b}`);
      root.style.setProperty("--ally-pink-hover", `${dark(r)} ${dark(g)} ${dark(b)}`);
      root.style.setProperty("--ally-soft", `${tint(r)} ${tint(g)} ${tint(b)}`);
    } else {
      root.style.removeProperty("--ally-pink");
      root.style.removeProperty("--ally-pink-hover");
      root.style.removeProperty("--ally-soft");
    }
  }, [branding, location.pathname]);

  const wl = branding && branding.white_label && branding.logo_url;

  const links = [
    { to: "/#pricing", label: "Pricing" },
    { to: "/pulse", label: "Market Pulse", badge: PULSE_NEW ? "New" : null },
    { to: "/insights", label: "Insights" },
    { to: "/refer", label: "Refer & earn" },
  ];

  const doLogout = () => {
    const dest = user && (user.role === "enterprise" || user.role === "superadmin") ? "/enterprise/login" : "/";
    logout();
    nav(dest);
  };

  return (
    <header className={`sticky top-0 z-50 ${dark ? "bg-ally-ink/80" : "bg-white/80"} backdrop-blur-xl border-b ${dark ? "border-white/10" : "border-black/5"}`}>
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to={wl ? "/enterprise/app" : "/"} data-testid="nav-logo" className="flex items-center gap-2">
          {wl ? (
            <img src={branding.logo_url} alt={branding.brand_name} className="h-8 w-auto" data-testid="nav-brand-logo" />
          ) : (
            <span className={`font-display font-black text-2xl tracking-tight ${text}`}>
              Defer<span className="text-ally-pink">Ally</span>
            </span>
          )}
        </Link>

        <div className="hidden md:flex items-center gap-7">
          {!user && links.map((l) => (
            l.badge ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                className="relative font-bold text-sm inline-flex items-center gap-1.5 group"
              >
                <Sparkles size={14} className="text-ally-pink animate-pulse" />
                <span className="bg-gradient-to-r from-ally-pink to-fuchsia-500 bg-clip-text text-transparent">{l.label}</span>
                <span className="ml-0.5 text-[9px] font-black uppercase tracking-wider text-white bg-ally-pink rounded-full px-1.5 py-0.5 leading-none animate-pulse">{l.badge}</span>
              </Link>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                className={`font-medium text-sm transition-colors hover:text-ally-pink ${
                  location.pathname === l.to ? "text-ally-pink" : sub
                }`}
              >
                {l.label}
              </Link>
            )
          ))}
          {user ? (
            <div className="flex items-center gap-4">
              {["investor", "enterprise"].includes(user.role) && <NotificationBell dark={dark} />}
              <Link to={(user.role === "superadmin" || user.role === "support" || user.role === "support_viewer") ? "/admin" : user.role === "enterprise" ? "/enterprise/app" : "/dashboard"} data-testid="nav-dashboard"
                className="bg-ally-pink text-white hover:bg-ally-pinkHover rounded-full px-5 py-2.5 font-bold text-sm transition-all flex items-center gap-2">
                <LayoutDashboard size={16} /> {(user.role === "superadmin" || user.role === "support" || user.role === "support_viewer") ? "Admin" : user.role === "enterprise" ? "Firm Portal" : "Dashboard"}
              </Link>
              <button data-testid="nav-logout" onClick={doLogout}
                className={`${sub} hover:text-ally-pink transition-colors`} aria-label="Log out">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Link to="/enterprise/login" data-testid="nav-enterprise-login" className={`font-medium text-sm flex items-center gap-1.5 ${sub} hover:text-ally-pink`}>
                <Building2 size={15} /> Enterprise login
              </Link>
              <Link to="/?demo=1" data-testid="nav-cta"
                className="bg-ally-pink text-white hover:bg-ally-pinkHover rounded-full px-6 py-3 font-bold text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-ally-pink/25">
                Book a Demo
              </Link>
            </div>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className={`md:hidden ${text}`} onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className={`md:hidden px-6 pb-6 flex flex-col gap-4 ${dark ? "bg-ally-ink" : "bg-white"}`}>
          {!user && links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={`font-medium ${l.badge ? "text-ally-pink font-bold" : sub} hover:text-ally-pink flex items-center gap-1.5`}>
              {l.badge && <Sparkles size={14} className="text-ally-pink" />}{l.label}
              {l.badge && <span className="text-[9px] font-black uppercase tracking-wider text-white bg-ally-pink rounded-full px-1.5 py-0.5 leading-none">{l.badge}</span>}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="bg-ally-pink text-white rounded-full px-6 py-3 font-bold text-sm text-center">
                Dashboard
              </Link>
              <button onClick={() => { setOpen(false); doLogout(); }} className={`${sub} text-left`}>Log out</button>
            </>
          ) : (
            <>
              <Link to="/enterprise/login" onClick={() => setOpen(false)} className={`${sub} flex items-center gap-1.5`}><Building2 size={15} /> Enterprise login</Link>
              <Link to="/?demo=1" onClick={() => setOpen(false)} className="bg-ally-pink text-white rounded-full px-6 py-3 font-bold text-sm text-center">
                Book a Demo
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};
